import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL  = 'https://vmdproject.com';
const SITE_NAME = 'VMDProject';
const OG_IMAGE  = `${SITE_URL}/og-banner.jpg`;

export function PageSEO({ title, description, canonical, ogImage, jsonLd, noindex }) {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} | Sviluppo App Mobile, SaaS e Web App`;

  const url = canonical ? `${SITE_URL}${canonical}` : SITE_URL;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex
        ? <meta name="robots" content="noindex" />
        : <link rel="canonical" href={url} />}

      {/* Open Graph */}
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url"         content={url} />
      <meta property="og:image"       content={ogImage || OG_IMAGE} />
      <meta property="og:type"        content="website" />
      <meta property="og:site_name"   content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={ogImage || OG_IMAGE} />

      {/* JSON-LD schema specifico per pagina */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

/* ─── Schema helpers ─── */

// Nodi definiti nel grafo statico di public/index.html: qui si referenziano soltanto.
const ORG    = { '@id': `${SITE_URL}/#org` };
const PERSON = { '@id': `${SITE_URL}/#person` };

// `price` va passato solo per app realmente scaricabili: un'offerta a 0 € su un'app in beta è fuorviante.
export function softwareAppSchema({ name, description, url, operatingSystem, applicationCategory, image, price, priceCurrency = 'EUR' }) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'SoftwareApplication',
        '@id': `${url}#app`,
        name,
        description,
        url,
        operatingSystem,
        applicationCategory,
        ...(image && { image: `${SITE_URL}${image}` }),
        ...(price !== undefined && { offers: { '@type': 'Offer', price, priceCurrency } }),
        author: PERSON,
        publisher: ORG,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Progetti', item: `${SITE_URL}/progetti` },
          { '@type': 'ListItem', position: 3, name, item: url },
        ],
      },
    ],
  };
}

export function serviceSchema(services) {
  return {
    '@context': 'https://schema.org',
    '@graph': services.map(s => ({
      '@type': 'Service',
      name: s.name,
      description: s.description,
      provider: ORG,
      areaServed: 'IT',
    })),
  };
}
